import {
  parse as dateFnsParse, parseISO, format as dateFnsFormat, formatDistance, startOfMonth, startOfWeek, endOfMonth,
  eachDayOfInterval, getYear, getMonth, getDate, setMonth, addMonths, addWeeks, addDays, subMonths,
  subDays, subHours, isBefore, isAfter, isToday, isEqual, isSameDay, isWithinInterval, isValid
} from 'date-fns';
import { convertPhpDateFormatToDateFns } from '../convert-date-format/index.js';

import { defaultLocale, locales } from './locales.js';
import { defaultFormatConfig, defaultPhpFormatConfig, getFormatsWithAliasesForConfig } from './formats.js';

const createInvalidDateObject = () => new Date('invalid');
const invalidDateString = createInvalidDateObject().toString();

const formats = getFormatsWithAliasesForConfig(defaultFormatConfig);
const getFormatsByUserConfig = ({ dateFormat, timeFormat }) => getFormatsWithAliasesForConfig({
  dateFormat: convertPhpDateFormatToDateFns(dateFormat),
  timeFormat: convertPhpDateFormatToDateFns(timeFormat)
});

const parse = (value) => {
  if (typeof value === 'string') {
    const parsedDate = parseISO(value);

    if (parsedDate.toString() !== invalidDateString) {
      return parsedDate;
    }
  }

  return new Date(value);
};

const parseByFormat = (value, phpFormat) => {
  const dateFnsFormat = convertPhpDateFormatToDateFns(phpFormat);

  return dateFnsParse(value, dateFnsFormat, new Date());
};

const parseWithoutTime = (value) => {
  if (!value) { return createInvalidDateObject(); }

  const parsedDate = parse(value);
  parsedDate.setHours(0);
  parsedDate.setMinutes(0);
  parsedDate.setSeconds(0);

  return parsedDate;
};

const parseWithoutTimeByFormat = (value, phpFormat) => {
  const parsedDate = parseByFormat(value, phpFormat);
  parsedDate.setHours(0);
  parsedDate.setMinutes(0);
  parsedDate.setSeconds(0);

  return parsedDate;
};

const format = (date, formatString, options) => {
  const parsedDate = parse(date);

  if (parsedDate === undefined || parsedDate.toString() === invalidDateString) {
    return invalidDateString;
  }

  return dateFnsFormat(parsedDate, formatString, options);
};

const formatByName = (date, formatName, userConfig = { ...defaultPhpFormatConfig, language: defaultLocale }) => {
  const locale = getLocaleWithFallback(userConfig.language);
  const formats = getFormatsByUserConfig({ dateFormat: userConfig.dateFormat, timeFormat: userConfig.timeFormat });
  const formatString = formats[formatName] || formats.fullDateWithHoursAndMins;

  if (!date || !dateUtils.isValid(date)) {
    return date;
  }

  return format(date, formatString, { locale: locale.lang });
};

const formatByLocale = (date, { locale = defaultLocale, short = false } = {}) => {
  const localeObject = getLocaleWithFallback(locale);
  const formatString = short ? localeObject.shortFormat : localeObject.format;

  return format(date, formatString, { locale: localeObject.lang });
};

const getLocaleWithFallback = locale => {
  const localeName = locales[locale] ? locale : defaultLocale;
  return locales[localeName];
};

export const dateUtils = {
  createInvalidDateObject,
  invalidDateString,
  formats,
  getFormatsByUserConfig,
  locales,
  getLocaleWithFallback,

  parse,
  parseDate: (value) => {
    console.warn(
      '`dateUtils.parseDate(value)` is deprecated. ' +
      'Use `dateUtils.parse(value)` instead. (ui-framework-utils)'
    );
    return parse(value);
  },
  parseByFormat,
  parseWithoutTime,
  parseDateWithoutTime: (value, phpFormat) => {
    console.warn(
      '`dateUtils.parseDateWithoutTime(value, format)` is deprecated. ' +
      'Use `dateUtils.parseWithoutTime(value, format)` instead. (ui-framework-utils)'
    );
    return parseWithoutTime(value, phpFormat);
  },
  parseWithoutTimeByFormat,
  format,
  formatByLocale,
  formatByName,
  distanceInWords: (baseDate, date, options) => {
    console.warn(
      '`dateUtils.distanceInWords(baseDate, date, options)` is deprecated. ' +
      'Use `dateUtils.formatDistance(date, baseDate, options)` instead. (ui-framework-utils)'
    );
    return formatDistance(parse(baseDate), parse(date), options);
  },
  formatDistance: (date, baseDate, options) =>
    formatDistance(parse(baseDate), parse(date), options),
  startOfMonth: (date) =>
    startOfMonth(parse(date)),
  startOfWeek: (date, options) =>
    startOfWeek(parse(date), options),
  endOfMonth: (date) =>
    endOfMonth(parse(date)),
  eachDay: (startDate, endDate, options) => {
    console.warn(
      '`dateUtils.eachDay(start, end, options)` is deprecated. ' +
      'Use `dateUtils.eachDayOfInterval({ start, end }, options)` instead. (ui-framework-utils)'
    );
    return eachDayOfInterval({ start: parse(startDate), end: parse(endDate) }, options);
  },
  eachDayOfInterval: ({ start, end }, options) =>
    eachDayOfInterval({ start: parse(start), end: parse(end) }, options),
  getYear: (date) =>
    getYear(parse(date)),
  getMonth: (date) =>
    getMonth(parse(date)),
  getDate: (date) =>
    getDate(parse(date)),
  setMonth: (date, month) =>
    setMonth(parse(date), month),
  addMonths: (date, months) =>
    addMonths(parse(date), months),
  addWeeks: (date, weeks) =>
    addWeeks(parse(date), weeks),
  addDays: (date, days) =>
    addDays(parse(date), days),
  subMonths: (date, months) =>
    subMonths(parse(date), months),
  subDays: (date, days) =>
    subDays(parse(date), days),
  subHours: (date, hours) =>
    subHours(parse(date), hours),
  isBefore: (date, dateToCompare) =>
    isBefore(parse(date), parse(dateToCompare)),
  isAfter: (date, dateToCompare) =>
    isAfter(parse(date), parse(dateToCompare)),
  isToday: (date) =>
    isToday(parse(date)),
  isEqual: (dateLeft, dateRight) =>
    isEqual(parse(dateLeft), parse(dateRight)),
  isSameDay: (dateLeft, dateRight) =>
    isSameDay(parse(dateLeft), parse(dateRight)),
  isWithinRange: (date, start, end) => {
    console.warn(
      '`dateUtils.isWithinRange(date, start, end)` is deprecated. ' +
      'Use `dateUtils.isWithinInterval(date, { start, end })` instead. (ui-framework-utils)'
    );
    return isWithinInterval(parse(date), { start: parse(start), end: parse(end) });
  },
  isWithinInterval: (date, { start, end }) =>
    isWithinInterval(parse(date), { start: parse(start), end: parse(end) }),
  isValid: (date) =>
    isValid(parse(date))
};
